import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { FileText, Download, Printer } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  { value: 'January', label: 'January' },
  { value: 'February', label: 'February' },
  { value: 'March', label: 'March' },
  { value: 'April', label: 'April' },
  { value: 'May', label: 'May' },
  { value: 'June', label: 'June' },
  { value: 'July', label: 'July' },
  { value: 'August', label: 'August' },
  { value: 'September', label: 'September' },
  { value: 'October', label: 'October' },
  { value: 'November', label: 'November' },
  { value: 'December', label: 'December' },
];

const TransportFeeReport = () => {
  const [month, setMonth] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (month && month !== '__all__') params.append('month', month);
      if (year) params.append('year', year);
      const response = await apiClient.get(`/api/transport/reports/fee-report?${params.toString()}`);
      if (response.data) {
        setReport(response.data);
      } else {
        alert('Failed to generate report');
      }
    } catch (error) {
      alert('Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Generate year options (last 5 years to next year)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 7 }, (_, i) => currentYear - 5 + i);

  return (
    <div className="p-6 space-y-6">
      {/* Filter Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Transport Fee Report
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleGenerate} className="flex flex-wrap gap-4 items-end">
            <div className="w-48">
              <Label>Month</Label>
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger>
                  <SelectValue placeholder="All Months" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Months</SelectItem>
                  {MONTHS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-32">
              <Label>Year</Label>
              <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={loading}>
              <FileText className="w-4 h-4 mr-2" />
              {loading ? 'Generating...' : 'Generate Report'}
            </Button>
            {report && (
              <Button type="button" variant="outline" onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            )}
          </form>
        </CardContent>
      </Card>

      {report && (
        <>
          {/* Summary Card */}
          <Card>
            <CardHeader>
              <CardTitle>Collection Summary {month && `- ${month}`} {year}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-600">Total Collected</p>
                  <p className="text-2xl font-bold text-blue-700">₹{report.summary?.total_collected || 0}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-600">Student Payments</p>
                  <p className="text-lg font-bold text-green-700">{report.summary?.total_student_payments || 0}</p>
                  <p className="text-sm text-green-600">₹{report.summary?.student_collection || 0}</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-sm text-purple-600">Fine Collected</p>
                  <p className="text-lg font-bold text-purple-700">{report.summary?.total_fine_payments || 0}</p>
                  <p className="text-sm text-purple-600">₹{report.summary?.fine_collection || 0}</p>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="text-sm text-orange-600">Total Transactions</p>
                  <p className="text-2xl font-bold text-orange-700">{report.payments?.length || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Details Card */}
          <Card className="print-area">
            <CardHeader>
              <CardTitle>Payment Details</CardTitle>
            </CardHeader>
            <CardContent>
              {!report.payments || report.payments.length === 0 ? (
                <p className="text-center text-gray-500 p-4">No payments found for selected period.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3">Payment #</th>
                        <th className="text-left p-3">Student Name</th>
                        <th className="text-left p-3">Route</th>
                        <th className="text-left p-3">Month</th>
                        <th className="text-left p-3">Date</th>
                        <th className="text-left p-3">Fee Amount</th>
                        <th className="text-left p-3">Fine</th>
                        <th className="text-left p-3">Total</th>
                        <th className="text-left p-3">Mode</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.payments.map((payment, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="p-3 font-medium">{payment.payment_number}</td>
                          <td className="p-3">{payment.payer_name}</td>
                          <td className="p-3">{payment.route_name}</td>
                          <td className="p-3">{payment.month}/{payment.year}</td>
                          <td className="p-3">{payment.payment_date}</td>
                          <td className="p-3 text-green-600">₹{payment.amount}</td>
                          <td className="p-3 text-red-600">₹{payment.fine_amount || 0}</td>
                          <td className="p-3 font-medium">₹{(payment.amount || 0) + (payment.fine_amount || 0)}</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 text-xs rounded-full ${payment.payment_mode === 'Cash' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                              {payment.payment_mode}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-100 font-bold">
                        <td colSpan="5" className="p-3 text-right">Total:</td>
                        <td className="p-3 text-green-600">₹{report.payments.reduce((sum, p) => sum + (p.amount || 0), 0)}</td>
                        <td className="p-3 text-red-600">₹{report.payments.reduce((sum, p) => sum + (p.fine_amount || 0), 0)}</td>
                        <td className="p-3">₹{report.payments.reduce((sum, p) => sum + (p.amount || 0) + (p.fine_amount || 0), 0)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default TransportFeeReport;
